import * as React from 'react';

import _ from 'lodash';

import type { FilterOption, Filters } from 'bundles/enterprise-admin-drawers/components/types';
import type { OrganizationSkillSetsQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byOrganization_elements as SkillSet } from 'bundles/enterprise-admin-skills/components/__generated__/OrganizationSkillSetsQuery';

export type PaginationProps = {
  start: string;
  limit: number;
  currentPage: number;
  handlePageChange: (pageNumber: number) => void;
  handleLimitChange: (limit: number) => void;
};

type WithPaginationArgs = {
  limit: number;
};

type WithPaginationState = {
  start: string;
  limit: number;
};

export const withPagination =
  <Props extends {}>({ limit: initialLimit }: WithPaginationArgs) =>
  (Component: React.ComponentType<Props & PaginationProps>): React.ComponentType<Props> => {
    const componentName = Component.displayName || Component.name;
    return class extends React.Component<Props, WithPaginationState> {
      static displayName = componentName + 'WithPagination';

      state = {
        start: '0',
        limit: initialLimit,
      };

      handlePageChange = (pageNumber: number) => {
        const { limit } = this.state;
        const start = ((pageNumber - 1) * limit).toString();
        this.setState({ start });
      };

      handleLimitChange = (limit: number) => {
        this.setState({ limit, start: '0' });
      };

      render() {
        const { start, limit } = this.state;
        const currentPage = Math.floor(parseInt(start, 10) / limit) + 1;
        return (
          <Component
            {...this.props}
            limit={limit}
            start={start}
            handlePageChange={this.handlePageChange}
            handleLimitChange={this.handleLimitChange}
            currentPage={currentPage}
          />
        );
      }
    };
  };

type WithSearchAndSortingState = {
  searchText: string;
  sortBy: TSPSortBy;
};

type PropsFromCaller = {
  targetSkillProfiles?: ({ title: string; createdAt: number } | null)[];
};

export enum TSPSortBy {
  nameAscending = 'NAME_ASCENDING',
  nameDescending = 'NAME_DESCENDING',
  createdAtAscending = 'CREATED_AT_ASCENDING',
  createdAtDescending = 'CREATED_AT_DESCENDING',
}

export type WithSearchAndSortingProps = {
  handleSearchChange: (searchText: string) => void;
  handleSortByChange: (sortBy: TSPSortBy) => void;
} & WithSearchAndSortingState;

export const withTSPSearchAndSorting = <Props extends PropsFromCaller>(
  Component: React.ComponentType<Props & WithSearchAndSortingProps>
): React.ComponentType<Props> => {
  const componentName = Component.displayName || Component.name;
  return class extends React.Component<Props, WithSearchAndSortingState> {
    static displayName = componentName + 'WithTSPSearchAndSorting';

    state = {
      searchText: '',
      sortBy: TSPSortBy.nameAscending,
    };

    handleSearchChange = (searchText: string) => {
      this.setState({ searchText });
    };

    handleSortByChange = (sortBy: TSPSortBy) => {
      this.setState({ sortBy });
    };

    getSortedAndFilteredList = () => {
      const { targetSkillProfiles } = this.props;
      const { searchText, sortBy } = this.state;
      const filteredList = targetSkillProfiles?.filter((el) =>
        el?.title.toLowerCase().includes(searchText.toLowerCase())
      );

      switch (sortBy) {
        case TSPSortBy.nameAscending:
          return _.sortBy(filteredList, (el) => el?.title.toLowerCase());
        case TSPSortBy.nameDescending:
          return _.sortBy(filteredList, (el) => el?.title.toLowerCase()).reverse();
        case TSPSortBy.createdAtAscending:
          return _.sortBy(filteredList, (el) => el?.createdAt);
        case TSPSortBy.createdAtDescending:
          return _.sortBy(filteredList, (el) => el?.createdAt).reverse();
        default:
          return filteredList;
      }
    };

    render() {
      const { searchText, sortBy } = this.state;
      const targetSkillProfiles = this.getSortedAndFilteredList();

      return (
        <Component
          {...this.props}
          searchText={searchText}
          sortBy={sortBy}
          targetSkillProfiles={targetSkillProfiles}
          handleSearchChange={this.handleSearchChange}
          handleSortByChange={this.handleSortByChange}
        />
      );
    }
  };
};

export type SkillSetFilters = {
  businessFunctions: FilterOption[];
  jobRoles: FilterOption[];
  skillsetTypes: FilterOption[];
};

export type WithSkillSetFilteringState = {
  filters: Filters;
};

export type SkillSetPropsFromCaller = {
  targetSkillProfiles: SkillSet[];
};

export type WithSkillSetFilteringProps = {
  handleFilterChange: (filters: Filters) => void;
} & WithSkillSetFilteringState;

export const withSkillSetFiltering = <Props extends SkillSetPropsFromCaller>(
  Component: React.ComponentType<Props & WithSkillSetFilteringProps>
): React.ComponentType<Props> => {
  const componentName = Component.displayName || Component.name;

  return class extends React.Component<Props, WithSkillSetFilteringState> {
    static displayName = componentName + 'WithSkillSetFilter';

    constructor(props: Props) {
      super(props);

      this.state = {
        filters: {
          businessFunctions: [],
          occupations: [],
          goalTypes: [],
          academies: [],
        },
      };
    }

    filteredSkillSets = () => {
      const { targetSkillProfiles } = this.props;
      const { filters } = this.state;

      return targetSkillProfiles?.filter((skillSet: SkillSet) => {
        const hasIncludedGoalType = filters.goalTypes.length
          ? skillSet.goalType && filters.goalTypes.some((option) => option.id === skillSet.goalType)
          : true;
        const hasIncludedBusinessFunction = filters.businessFunctions.length
          ? skillSet.functionId && filters.businessFunctions.some((option) => option.id === skillSet.functionId)
          : true;
        const hasIncludedProficiencies = filters.occupations.length
          ? skillSet.occupationId && filters.occupations.some((option) => option.id === skillSet.occupationId)
          : true;
        const hasIncludedAcademy = filters.academies.length
          ? skillSet.academyIds && filters.academies.some((option) => skillSet.academyIds.includes(option.id))
          : true;

        return hasIncludedGoalType && hasIncludedBusinessFunction && hasIncludedProficiencies && hasIncludedAcademy;
      });
    };

    handleFilterChange = (filters: Filters) => {
      this.setState({ filters });
    };

    render() {
      const { filters } = this.state;
      const targetSkillProfiles = this.filteredSkillSets();

      return (
        <Component
          {...this.props}
          filters={filters}
          targetSkillProfiles={targetSkillProfiles}
          handleFilterChange={this.handleFilterChange}
        />
      );
    }
  };
};
